import { ReactElement } from "react";
import { AgGrid } from "@jsq/ds/core";
import { Money, MoneyFormat, MoneyType } from "~/components/Money";
import { CurrencyCode } from "~/graphql/mainTypes";

interface MoneyCellRendererProps {
  /** The value to display */
  value: number | string | null | undefined;
  /** The data object for the row */
  data?: Record<string, unknown>;
  /** Field name in data object that contains the currency code. If omitted, uses USD */
  currencyField?: string;
  /** Whether to round the value to the nearest whole number */
  rounded?: boolean;
  /** Format to use for the money display */
  format?: MoneyFormat;
}

/**
 * Extended AG Grid cell renderer params that includes our custom params
 */
interface MoneyCellRendererAgGridParams extends AgGrid.ICellRendererParams {
  cellRendererParams?: MoneyCellRendererProps;
}

/**
 * Money Cell Renderer for AG Grid
 * Renders a monetary value with the appropriate currency symbol
 *
 * If currencyField is provided, it will look for that field in the data object.
 * If currencyField is omitted or the field is not found, it will use USD as the default currency.
 */
export const MoneyCellRenderer = (
  props: MoneyCellRendererProps | MoneyCellRendererAgGridParams,
): ReactElement | string => {
  let value: unknown;
  let data: Record<string, unknown> | undefined;
  let currencyField: string | undefined;
  let rounded: boolean | undefined;
  let format: MoneyFormat | undefined;

  if ("node" in props) {
    const agGridSpecificProps = props; // props is at least MoneyCellRendererAgGridParams
    value = agGridSpecificProps.value;
    data = agGridSpecificProps.data as Record<string, unknown> | undefined;

    currencyField =
      (props as MoneyCellRendererProps).currencyField ??
      agGridSpecificProps.cellRendererParams?.currencyField;
    rounded =
      (props as MoneyCellRendererProps).rounded ??
      agGridSpecificProps.cellRendererParams?.rounded;
    format =
      (props as MoneyCellRendererProps).format ??
      agGridSpecificProps.cellRendererParams?.format;
  } else {
    const directProps = props;
    value = directProps.value;
    data = directProps.data;
    currencyField = directProps.currencyField;
    rounded = directProps.rounded;
    format = directProps.format;
  }

  if (value === null || value === undefined) {
    return "-";
  }

  const numericValue = typeof value === "number" ? value : Number(value);

  if (isNaN(numericValue)) {
    return "-";
  }

  // Default to USD currency when currencyField is not provided or not found in the data
  let currency = "USD";

  if (currencyField && data && typeof data === "object") {
    const dataRecord = data;

    if (
      currencyField in dataRecord &&
      dataRecord[currencyField] !== undefined &&
      dataRecord[currencyField] !== null
    ) {
      currency = String(dataRecord[currencyField]);
    }
  }

  const money: MoneyType = {
    amount: Math.round(numericValue * 100),
    currency: currency.toUpperCase() as CurrencyCode,
    precision: 2,
  };

  return (
    <Money
      money={money}
      format={format || MoneyFormat.Symbol}
      dashesAsDefault
      rounded={rounded !== undefined ? rounded : false}
    />
  );
};
